import {
  Paper,
  Title,
  Stack,
  Card,
  Text,
  Group,
  Checkbox,
  Badge,
  Box,
  TextInput,
  Accordion,
  Grid,
} from "@mantine/core"
import { UseFormReturnType } from "@mantine/form"
import { IconSearch, IconChevronRight } from "@tabler/icons-react"
import { useCallback, useMemo, useState } from "react"

import {
  AxesFeatureLabel,
  LabelsExplorerResponses,
} from "@costory/types/endpoints/labelsExplorer"

import { formatNumber } from "@costory/shared/utils/format"

import { MapValues } from "@costory/front/pages/LabelsExplorer/components/MapValues"
import {
  SortHeader,
  type SortBy,
  type SortOrder,
} from "@costory/front/pages/LabelsExplorer/components/SortHeader"
import { MergeLabelsStep } from "@costory/front/pages/LabelsExplorer/components/mergeLabelsStep"
import { NameLabelStep } from "@costory/front/pages/LabelsExplorer/components/nameLabelStep"
import { StepType } from "@costory/front/pages/LabelsExplorer/components/stepTypes"

export function CombinedLabelsPanel({
  labels,
  searchTerm,
  onSearchChange,
  form,
  onAxesFeatureLabelAdd,
  allLabels,
  completedSteps,
  setCompletedSteps,
  openSteps,
  setOpenSteps,
  editingLabel,
}: {
  labels: LabelsExplorerResponses.LabelsStatsFullBilling[]
  searchTerm: string
  onSearchChange: (value: string) => void
  form: UseFormReturnType<LabelsExplorerResponses.AxesFeatureLabelForm>
  onAxesFeatureLabelAdd: (axesFeatureLabel: AxesFeatureLabel) => void
  allLabels: LabelsExplorerResponses.LabelsStatsFullBilling[]
  completedSteps: Record<StepType, AxesFeatureLabel | null>
  setCompletedSteps: (
    completedSteps: Record<StepType, AxesFeatureLabel | null>,
  ) => void
  openSteps: Record<StepType, boolean>
  setOpenSteps: (openSteps: Record<StepType, boolean>) => void
  editingLabel: AxesFeatureLabel | null
}) {
  const selectedKeys = useMemo(
    () => form.values.columnsMerged.map((col) => col.name) || [],
    [form.values.columnsMerged],
  )
  const [sortBy, setSortBy] = useState<SortBy | null>("cost")
  const [sortOrder, setSortOrder] = useState<SortOrder | null>("desc")

  const handleStepConfirm = useCallback(
    (step: StepType, data: AxesFeatureLabel | null) => {
      setCompletedSteps({
        ...completedSteps,
        [step]: data,
      })

      if (step === StepType.MERGE) {
        form.setFieldValue("columnsMerged", data?.columnsMerged || [])
        setOpenSteps({
          [StepType.MERGE]: false,
          [StepType.MAP]: true,
          [StepType.NAME]: false,
        })
      }
      if (step === StepType.MAP) {
        form.setFieldValue("maps", data?.maps || [])
        setOpenSteps({
          [StepType.MERGE]: false,
          [StepType.MAP]: false,
          [StepType.NAME]: true,
        })
      }
      if (step === StepType.NAME) {
        if (data && onAxesFeatureLabelAdd) {
          const isMultiLabel = selectedKeys.length > 1
          if (isMultiLabel && completedSteps[StepType.MERGE]) {
            const mergeData = completedSteps[StepType.MERGE]!
            const finalAxesFeatureLabel: AxesFeatureLabel = {
              name: data.name,
              columnsMerged: mergeData.columnsMerged,
              maps: data.maps,
            }
            onAxesFeatureLabelAdd(finalAxesFeatureLabel)
          } else {
            onAxesFeatureLabelAdd(data)
          }

          // Reset form and steps
          form.reset()
          setCompletedSteps({
            [StepType.MERGE]: null,
            [StepType.MAP]: null,
            [StepType.NAME]: null,
          })
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedKeys, completedSteps, onAxesFeatureLabelAdd, form],
  )

  const handleLabelToggle = useCallback(
    (
      label: LabelsExplorerResponses.LabelsStatsFullBilling,
      checked: boolean,
    ) => {
      const currentColumnsMerged = form.values.columnsMerged || []

      let updatedColumnsMerged
      if (checked) {
        updatedColumnsMerged = [
          ...currentColumnsMerged,
          {
            name: label.key,
            type: "label" as const,
            ingested: false,
          },
        ]
      } else {
        updatedColumnsMerged = currentColumnsMerged.filter(
          (col) => col.name !== label.key,
        )
      }

      form.setFieldValue("columnsMerged", updatedColumnsMerged)

      setCompletedSteps({
        [StepType.MERGE]: null,
        [StepType.NAME]: null,
        [StepType.MAP]: null,
      })

      // Auto-open the first relevant step when labels are selected
      if (checked && updatedColumnsMerged.length === 1) {
        // Single label - start with Map Values
        setOpenSteps({
          [StepType.MERGE]: false,
          [StepType.MAP]: true,
          [StepType.NAME]: false,
        })
      } else if (checked && updatedColumnsMerged.length > 1) {
        // Multiple labels selected - start with Merge Labels
        setOpenSteps({
          [StepType.MERGE]: true,
          [StepType.MAP]: false,
          [StepType.NAME]: false,
        })
      } else if (!checked && updatedColumnsMerged.length === 0) {
        // No labels selected - close all steps
        setOpenSteps({
          [StepType.MERGE]: false,
          [StepType.MAP]: false,
          [StepType.NAME]: false,
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [form],
  )

  const isMultiLabel = selectedKeys.length > 1
  const hasSelection = selectedKeys.length > 0

  const handleSort = (column: SortBy) => {
    if (sortBy === column) {
      if (sortOrder === "asc") {
        setSortOrder("desc")
      } else if (sortOrder === "desc") {
        setSortBy(null)
        setSortOrder(null)
      } else {
        setSortOrder("asc")
      }
    } else {
      setSortBy(column)
      setSortOrder("asc")
    }
  }

  const filteredLabels = useMemo(() => {
    if (!searchTerm.trim()) return labels
    const searchLower = searchTerm.toLowerCase()
    return labels.filter((label) =>
      label.key.toLowerCase().includes(searchLower),
    )
  }, [labels, searchTerm])

  const sortedLabels = useMemo(() => {
    if (!sortBy || !sortOrder) return filteredLabels

    return [...filteredLabels].sort((a, b) => {
      if (sortBy === "cost") {
        const costA = a.total_cost_key_present ?? 0
        const costB = b.total_cost_key_present ?? 0
        return sortOrder === "asc" ? costA - costB : costB - costA
      }
      return sortOrder === "asc"
        ? a.key.localeCompare(b.key)
        : b.key.localeCompare(a.key)
    })
  }, [filteredLabels, sortBy, sortOrder])

  return (
    <Paper
      p="md"
      h="calc(100vh - 160px)"
      withBorder
      style={{ display: "flex", flexDirection: "column" }}
    >
      <Grid style={{ flex: 1 }}>
        <Grid.Col span={6} style={{ display: "flex", flexDirection: "column" }}>
          <Stack gap="md">
            <Title order={4}>Source Labels</Title>
            <TextInput
              placeholder="Search source labels..."
              leftSection={<IconSearch size={16} />}
              value={searchTerm}
              onChange={(e) => onSearchChange(e.currentTarget.value)}
              size="sm"
            />
            <Group justify="space-between" align="center">
              <SortHeader
                label="LABELS"
                column="name"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />
              <SortHeader
                label="TOTAL COST KEY PRESENT"
                column="cost"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />
            </Group>
            {isMultiLabel && (
              <Text size="sm" c="primaryHighlight">
                {selectedKeys.length} source labels selected for transformation
              </Text>
            )}
            {selectedKeys.length === 1 && (
              <Text size="sm" c="primaryHighlight">
                1 source label selected for transformation
              </Text>
            )}
          </Stack>

          <Box
            flex={1}
            mah="calc(100vh - 330px)"
            pr="4px"
            style={{ overflowY: "auto" }}
          >
            <Stack gap="xs">
              {sortedLabels.map(
                (label: LabelsExplorerResponses.LabelsStatsFullBilling) => {
                  const isChecked = selectedKeys.includes(label.key)
                  const isIngested = label.ingested

                  return (
                    <Card
                      key={label.key}
                      withBorder
                      p="sm"
                      radius="sm"
                      style={{
                        cursor: "pointer",
                      }}
                      bg={isChecked ? "surfaceSelected" : "transparent"}
                      bd={isChecked ? "1px solid primaryHighlight" : undefined}
                    >
                      <Group gap="sm" wrap="nowrap">
                        <Checkbox
                          checked={isChecked}
                          onChange={(e) => {
                            e.stopPropagation()
                            handleLabelToggle(label, e.currentTarget.checked)
                          }}
                        />
                        <Text
                          size="sm"
                          truncate
                          style={{
                            flex: 1,
                          }}
                          c={
                            isChecked
                              ? "primaryHighlight"
                              : isIngested
                                ? "textSecondary"
                                : undefined
                          }
                        >
                          {label.key}
                        </Text>
                        <Badge variant="light" color="textSecondary" size="xs">
                          {formatNumber({
                            value: label.total_cost_key_present ?? 0,
                          })}
                        </Badge>
                        {isIngested && (
                          <Badge
                            variant="light"
                            color="primaryHighlight"
                            size="xs"
                          >
                            Ingested
                          </Badge>
                        )}
                      </Group>
                    </Card>
                  )
                },
              )}
            </Stack>
          </Box>
        </Grid.Col>

        <Grid.Col
          span={6}
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Stack gap="md">
            <Title order={4}>Transformation Steps</Title>

            {!hasSelection ? (
              <Paper p="md" withBorder>
                <Text size="sm" c="dimmed" ta="center">
                  Select one or more source labels to begin transformations
                </Text>
              </Paper>
            ) : (
              <Accordion
                multiple={false}
                value={
                  Object.keys(openSteps).find(
                    (step) => openSteps[step as StepType],
                  ) || undefined
                }
                onChange={(value) => {
                  if (value) {
                    // Close all steps and open only the selected one
                    const newOpenSteps = Object.keys(openSteps).reduce(
                      (acc, step) => {
                        acc[step as StepType] = step === value
                        return acc
                      },
                      {} as Record<StepType, boolean>,
                    )
                    setOpenSteps(newOpenSteps)
                  } else {
                    // If no value is selected, close all steps
                    const newOpenSteps = Object.keys(openSteps).reduce(
                      (acc, step) => {
                        acc[step as StepType] = false
                        return acc
                      },
                      {} as Record<StepType, boolean>,
                    )
                    setOpenSteps(newOpenSteps)
                  }
                }}
              >
                {/* Merge Labels Step - only show for multiple labels */}
                {isMultiLabel && (
                  <Accordion.Item value={StepType.MERGE}>
                    <Accordion.Control
                      icon={
                        <IconChevronRight
                          size={16}
                          style={{
                            transform: openSteps[StepType.MERGE]
                              ? "rotate(90deg)"
                              : "rotate(0deg)",
                            transition: "transform 0.2s ease",
                          }}
                        />
                      }
                    >
                      <Group gap="xs">
                        <Text size="sm" fw={500}>
                          Step 1: Merge Labels
                        </Text>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      <MergeLabelsStep
                        labels={allLabels.filter((l) =>
                          selectedKeys.includes(l.key),
                        )}
                        onConfirm={(data) =>
                          handleStepConfirm(StepType.MERGE, data)
                        }
                        completed={completedSteps[StepType.MERGE] || undefined}
                      />
                    </Accordion.Panel>
                  </Accordion.Item>
                )}

                {/* Step 2: Map Values - always shown after merge or directly if single label */}
                <Accordion.Item value={StepType.MAP}>
                  <Accordion.Control
                    icon={
                      <IconChevronRight
                        size={16}
                        style={{
                          transform: openSteps[StepType.MAP]
                            ? "rotate(90deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.2s ease",
                        }}
                      />
                    }
                  >
                    <Group gap="xs">
                      <Text size="sm" fw={500}>
                        {isMultiLabel
                          ? "Step 2: Map Values"
                          : "Step 1: Map Values"}
                      </Text>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <MapValues
                      allValues={(() => {
                        const valuesSet = new Set<string>()
                        allLabels
                          .filter((label) => selectedKeys.includes(label.key))
                          .forEach((label) => {
                            label.values?.forEach((value) => {
                              valuesSet.add(value.value)
                            })
                          })
                        return Array.from(valuesSet)
                      })()}
                      onConfirm={(data) =>
                        handleStepConfirm(StepType.MAP, data)
                      }
                      completed={completedSteps[StepType.MAP] || undefined}
                    />
                  </Accordion.Panel>
                </Accordion.Item>

                {/* Step 3: Name Label */}
                <Accordion.Item value={StepType.NAME}>
                  <Accordion.Control
                    icon={
                      <IconChevronRight
                        size={16}
                        style={{
                          transform: openSteps[StepType.NAME]
                            ? "rotate(90deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.2s ease",
                        }}
                      />
                    }
                  >
                    <Group gap="xs">
                      <Text size="sm" fw={500}>
                        {isMultiLabel
                          ? "Step 3: Name Label"
                          : "Step 2: Name Label"}
                      </Text>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <NameLabelStep
                      allLabels={allLabels}
                      selectedItem={selectedKeys}
                      onConfirm={(data) =>
                        handleStepConfirm(StepType.NAME, data)
                      }
                      completed={completedSteps[StepType.NAME] || undefined}
                      mapping={completedSteps[StepType.MAP]?.maps || []}
                      editingLabel={editingLabel}
                    />
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            )}
          </Stack>
        </Grid.Col>
      </Grid>
    </Paper>
  )
}
